import {call, put, takeLatest, delay, select} from "redux-saga/effects";
import {
    addUserStart,
    addUserSuccessOrFailure,
    changeRole,
    deleteUser,
    getUserFailure,
    getUsers,
    getUserSuccess, removeAdminRole, resUser, setIsChangingRole
} from "../../reducers/UserReducer";
import {userApiCall} from "../../apiCall/userApiCall";
import {apiCall} from "../../apiCall/apiCall";
import {store} from "../../store";
import {toast} from "react-toastify";

const getIdToken = () => store.getState().authReducer.idToken;

// Selector to get current pagination state
const getUserPaginationState = (state) => ({
    currentPage: state.userReducer.currentPage,
    pageSize: state.userReducer.pageSize,
    search: state.userReducer.search
});

function* fetchUsersAndRoles(action) {
    try {
        // Get current pagination state
        const { currentPage, pageSize, search } = yield select(getUserPaginationState);
        
        // Use action payload if provided, otherwise use current state
        const page = action.payload?.page || currentPage;
        const size = action.payload?.pageSize || pageSize;
        const searchTerm = action.payload?.search !== undefined ? action.payload.search : search;
        
        // Calculate pagination parameters for Auth0 API
        const from = (page - 1) * size;
        const to = from + size - 1;
        
        // Build the API URL with pagination and search
        let apiUrl = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/api/v2/users?page=${page - 1}&per_page=${size}&include_totals=true`;
        
        // Add search query if provided
        if (searchTerm && searchTerm.trim()) {
            // Auth0 uses Lucene query syntax for searching
            const searchQuery = encodeURIComponent(`email:*${searchTerm.trim()}* OR name:*${searchTerm.trim()}* OR nickname:*${searchTerm.trim()}*`);
            apiUrl += `&q=${searchQuery}`;
        }
        
        console.log('Fetching users with URL:', apiUrl);
        
        const response = yield call(userApiCall, apiUrl, "get");
        
        // Auth0 returns data in this format when include_totals=true
        const users = response.data.users || response.data;
        const total = response.data.total || users.length;
        
        yield put(getUserSuccess({
            users: users,
            total: total
        }));
        
    } catch (error) {
        console.error('Error fetching users:', error);
        yield put(getUserFailure(error.message));
    } finally {
        yield put(setIsChangingRole())
    }
}

export function* workDeleteUser(action) {
    let userId = action.payload.id
    try {
        yield call(userApiCall, `https://${process.env.REACT_APP_AUTH0_DOMAIN}/api/v2/users/${userId}`, "delete");
        toast.success("User deleted successfully");
        yield delay(2000)
        
        // Refresh current page after deletion
        const { currentPage, pageSize, search } = yield select(getUserPaginationState);
        yield put(getUsers({ page: currentPage, pageSize, search }));
        
    } catch ({message}) {
        yield put(getUserFailure(message));
    }
}

export function* workAddUser(action) {
    try {
        yield call(userApiCall, `https://${process.env.REACT_APP_AUTH0_DOMAIN}/api/v2/users`, "post",
            {
                email: action.payload.email,
                password: action.payload.password,
                username: action.payload.username,
                email_verified: action.payload.emailVerified,
                connection: 'Username-Password-Authentication'
            });
        toast.success("User created successfully !");
        action.payload.onClose();
        yield delay(2000)
        // Refresh current page after adding user
        const { currentPage, pageSize, search } = yield select(getUserPaginationState);
        yield put(getUsers({ page: currentPage, pageSize, search }));
        yield put(resUser(""));
    } catch (err) {
        const errorMessage = err.response?.data?.message || "An unexpected error occurred";
        toast.error(errorMessage);
        yield put(getUserFailure(err));
    } finally {
        yield put(addUserSuccessOrFailure())
    }
}

export function* workChangeRole(action) {
    let {userId, role} = action.payload
    try {
        yield call(apiCall, `/auth/assign_role/${userId}`, "patch", role, getIdToken());
        yield delay(2000)
        
        // Refresh current page after role change
        const { currentPage, pageSize, search } = yield select(getUserPaginationState);
        yield put(getUsers({ page: currentPage, pageSize, search }));
        
    } catch ({message}) {
        yield put(getUserFailure(message));
    }
}

export function* workRemoveAdminRole(action) {
    const {userId, roleId} = action.payload
    try {
        yield call(apiCall, `/auth/${userId}/remove_role/${roleId}`, "delete", null, getIdToken());
        yield delay(2000)
        
        // Refresh current page after role removal
        const { currentPage, pageSize, search } = yield select(getUserPaginationState);
        yield put(getUsers({ page: currentPage, pageSize, search }));
        
    } catch ({message}) {
        yield put(getUserFailure(message));
    }
}

export function* userSaga() {
    yield takeLatest(addUserStart().type, workAddUser);
    yield takeLatest(removeAdminRole().type, workRemoveAdminRole);
    yield takeLatest(changeRole().type, workChangeRole);
    yield takeLatest(getUsers().type, fetchUsersAndRoles);
    yield takeLatest(deleteUser().type, workDeleteUser)
}